import React, { useRef, useState } from 'react';
import clsx from 'clsx';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import type { SplitLayout } from '@hooks/usePageLayout/pageLayout';
import SvgRingilWordmark from '@icons/ringil-wordmark.svg?react';
import { ratioFromPointer } from './splitLogic';

interface ComponentProps {
  /** `vertical`: list and detail side by side; `horizontal`: list above the detail. */
  layout: SplitLayout;
  /** The list pane's share in %. */
  ratio: number;
  /** The divider was dragged and released at this ratio. */
  onRatioChange: (ratio: number) => void;
  list: React.ReactNode;
  detail: React.ReactNode;
}

/**
 * The two panes of the split view (Figma 21940:60880; the stacked variant was agreed without a
 * design). They fill the content column below the page title and scroll on their own, so the list
 * keeps its place while the detail is read. The divider between them is dragged to resize, and the
 * ratio is reported on release only.
 *
 * The fill is flex (the app's content column and its inner wrapper are flex columns), not a height
 * measured from the window: that ignored what sits below the workspace (the version switch link, the
 * column's gap), so the column overflowed and scrolled the whole page beside the panes' own scroll bars.
 */
const SplitWorkspace: React.FC<ComponentProps> = ({ layout, ratio, onRatioChange, list, detail }) => {
  const { tr } = useTranslationLgs();
  const containerRef = useRef<HTMLDivElement>(null);
  const [dragRatio, setDragRatio] = useState<number | null>(null);
  const side = layout === 'vertical';
  const shown = dragRatio ?? ratio;

  const pointerRatio = (event: React.PointerEvent) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return ratio;
    return side ? ratioFromPointer(rect.left, rect.width, event.clientX, ratio) : ratioFromPointer(rect.top, rect.height, event.clientY, ratio);
  };

  const line = clsx(
    'flex-1 rounded-full bg-content-gr2 transition-colors group-hover:bg-content-brand',
    side ? 'w-0.5' : 'h-0.5',
    dragRatio !== null && 'bg-content-brand',
  );

  return (
    <div
      ref={containerRef}
      className={clsx('mb-2 flex min-h-[320px] w-full flex-1 basis-0', side ? 'flex-row' : 'flex-col')}
      data-testid="split-workspace"
      data-layout={layout}
    >
      {/* Side by side, the list keeps the detail pane's inner padding (pl-2) towards the line, so both sit at the same distance from it. */}
      {/* Each pane is `relative`: absolutely positioned helpers inside (sr-only and PrimeReact's p-hidden-accessible
        inputs) must take the pane as their containing block. Otherwise they are placed against the content column,
        escape the pane's scroll clip at their position deep in the content, and make the whole page scroll. */}
      <div className={clsx('relative min-h-0 min-w-0 overflow-auto', side && 'pr-2')} style={{ flex: `0 0 ${shown}%` }} data-testid="split-list">
        {list}
      </div>
      <div
        role="separator"
        aria-orientation={side ? 'vertical' : 'horizontal'}
        aria-valuenow={Math.round(shown)}
        aria-label={tr('RingilDataTable.resizePanes', 'Drag to resize the panes')}
        onPointerDown={event => {
          event.preventDefault();
          event.currentTarget.setPointerCapture(event.pointerId);
          setDragRatio(pointerRatio(event));
        }}
        onPointerMove={event => dragRatio !== null && setDragRatio(pointerRatio(event))}
        onPointerUp={event => {
          if (dragRatio === null) return;
          const next = pointerRatio(event);
          setDragRatio(null);
          onRatioChange(next);
        }}
        className={clsx('group flex shrink-0 touch-none items-center justify-center', side ? 'w-3.5 flex-col cursor-col-resize' : 'h-7 flex-row cursor-row-resize')}
        data-testid="split-divider"
      >
        {/* The line, broken in the middle by the RINGIL wordmark, like the watermark beside narrow content. */}
        <span className={line} />
        {/* The SVG is drawn upright; below the list it is turned to read along the line, in a box of its turned size. */}
        <span aria-hidden className={clsx('flex shrink-0 items-center justify-center', side ? 'my-2 h-16 w-3.5' : 'mx-2 h-3.5 w-16')}>
          <SvgRingilWordmark
            className={clsx(
              'h-16 w-auto shrink-0 text-content-quaternary transition-colors group-hover:text-content-brand',
              !side && 'rotate-90',
              dragRatio !== null && 'text-content-brand',
            )}
          />
        </span>
        <span className={line} />
      </div>
      <div className="relative min-h-0 min-w-0 flex-1 overflow-auto" data-testid="split-detail">
        {detail}
      </div>
    </div>
  );
};

export default SplitWorkspace;
